/**
 * PricingContext - Dynamic pricing configuration from Firestore
 * 
 * Provides real-time pricing data for plans and add-ons across the app.
 * Admins can update pricing via the PricingManager component.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, getArtifactProjectId } from '../firebaseInit';

// Default pricing (used as fallback and initial seed)
export const DEFAULT_PLANS = {
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small teams getting started',
    monthlyLimit: 25,
    agentLimit: 3,
    monthlyCost: 99,
    yearlyCost: 1089,
    features: [
      'Team lead + 3 agents',
      '25 call evaluations/month',
      'Full script builder & flowchart editor',
      'Lead capture & leads management',
      'Performance dashboard',
      'Marketplace: browse & buy scripts',
      'Email support'
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For growing teams that need more capacity',
    monthlyLimit: 100,
    agentLimit: 10,
    monthlyCost: 199,
    yearlyCost: 2189,
    popular: true,
    features: [
      'Everything in Starter, plus:',
      'Team lead + 10 agents',
      '100 call evaluations/month',
      'AI Script Builder',
      'AI Script Improvement suggestions',
      'Marketplace: sell your scripts',
      'Priority support'
    ]
  },
  pro_plus: {
    id: 'pro_plus',
    name: 'Business',
    description: 'For established teams running serious operations',
    monthlyLimit: 300,
    agentLimit: 25,
    monthlyCost: 399,
    yearlyCost: 4389,
    whiteLabelIncluded: true,
    features: [
      'Everything in Pro, plus:',
      'Team lead + 25 agents',
      '300 call evaluations/month',
      'Per-script lead capture forms',
      'White label branding included',
      'Dedicated support'
    ]
  },
  platinum: {
    id: 'platinum',
    name: 'Platinum',
    description: 'Custom setup for large brokerages and investment operations',
    monthlyLimit: null,
    agentLimit: null,
    monthlyCost: null,
    isCustom: true,
    features: [
      'Everything in Business, plus:',
      '25+ agents (custom)',
      'Custom evaluation volume',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee',
      'Annual contracts available'
    ]
  }
};

export const DEFAULT_ADDONS = {
  white_label: {
    id: 'white_label',
    name: 'White Labeling',
    description: 'Custom branding for your team',
    monthlyCost: 149,
    type: 'recurring'
  },
  extra_agent: {
    id: 'extra_agent',
    name: 'Additional Agent',
    description: 'Add one more agent to your team',
    monthlyCost: 20,
    type: 'recurring'
  },
  eval_pack_10: {
    id: 'eval_pack_10',
    name: '10 Evaluation Credits',
    description: '10 additional call evaluations',
    price: 15,
    credits: 10,
    pricePerCredit: 1.50,
    type: 'one_time'
  },
  eval_pack_20: {
    id: 'eval_pack_20',
    name: '20 Evaluation Credits',
    description: '20 additional call evaluations',
    price: 25,
    credits: 20,
    pricePerCredit: 1.25,
    type: 'one_time',
    savings: '17%'
  },
  eval_pack_50: {
    id: 'eval_pack_50',
    name: '50 Evaluation Credits',
    description: '50 additional call evaluations',
    price: 55,
    credits: 50,
    pricePerCredit: 1.10,
    type: 'one_time',
    savings: '27%',
    popular: true
  },
  eval_pack_100: {
    id: 'eval_pack_100',
    name: '100 Evaluation Credits',
    description: '100 additional call evaluations',
    price: 100,
    credits: 100,
    pricePerCredit: 1.00,
    type: 'one_time',
    savings: '33%'
  }
};

const PricingContext = createContext();

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};

export const PricingProvider = ({ children }) => {
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [addons, setAddons] = useState(DEFAULT_ADDONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load pricing from Firestore with real-time updates
  useEffect(() => {
    const appId = getArtifactProjectId();
    const pricingRef = doc(db, `artifacts/${appId}/global/pricing`);

    const unsubscribe = onSnapshot(
      pricingRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          if (data.plans) {
            setPlans({ ...DEFAULT_PLANS, ...data.plans });
          }
          if (data.addons) {
            setAddons({ ...DEFAULT_ADDONS, ...data.addons });
          }
          if (data.updatedAt) {
            setLastUpdated(data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt));
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading pricing:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Save pricing to Firestore (admin only)
  const savePricing = async (newPlans, newAddons) => {
    try {
      const appId = getArtifactProjectId();
      const pricingRef = doc(db, `artifacts/${appId}/global/pricing`);
      
      await setDoc(pricingRef, {
        plans: newPlans,
        addons: newAddons,
        updatedAt: new Date(),
        updatedBy: 'admin' // Could include actual admin email
      });
      
      return { success: true };
    } catch (err) {
      console.error('Error saving pricing:', err);
      return { success: false, error: err.message };
    }
  };

  // Helper to get plan by ID
  const getPlan = (planId) => plans[planId] || DEFAULT_PLANS[planId] || null;

  // Helper to get addon by ID
  const getAddon = (addonId) => addons[addonId] || DEFAULT_ADDONS[addonId] || null;

  // Get paid plans only (excluding trial)
  const getPaidPlans = () => {
    return ['starter', 'pro', 'pro_plus'].map(id => plans[id]).filter(Boolean);
  };

  // Get evaluation credit packs
  const getEvalPacks = () => {
    return Object.values(addons).filter(a => a.type === 'one_time' && a.id.startsWith('eval_pack'));
  };

  // Get recurring addons
  const getRecurringAddons = () => {
    return Object.values(addons).filter(a => a.type === 'recurring');
  };

  const value = {
    plans,
    addons,
    loading,
    error,
    lastUpdated,
    savePricing,
    getPlan,
    getAddon,
    getPaidPlans,
    getEvalPacks,
    getRecurringAddons,
    DEFAULT_PLANS,
    DEFAULT_ADDONS
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export default PricingContext;
